/**
 * RBAC Role & Access Level Definitions
 *
 * Defines the 5 platform roles and 5 access levels used across the
 * XS+ Billing Platform. Includes a legacy role map for backward
 * compatibility with existing role values.
 *
 * Source of truth: docs/rbac/05-role-permission-matrix.md
 *
 * This module is shared between the admin portal frontend and the backend API.
 */

// ---------------------------------------------------------------------------
// UserRole — the 5 platform roles
// ---------------------------------------------------------------------------

export type UserRole =
  | 'super_admin'
  | 'admin'
  | 'normal_user'
  | 'finance_user'
  | 'developer';

/** All valid user roles as a runtime array for validation. */
export const ALL_ROLES: readonly UserRole[] = [
  'super_admin',
  'admin',
  'normal_user',
  'finance_user',
  'developer',
] as const satisfies readonly UserRole[];

// ---------------------------------------------------------------------------
// AccessLevel — granularity of access for a role/permission pair
// ---------------------------------------------------------------------------

export type AccessLevel =
  | 'full'
  | 'view_only'
  | 'own_only'
  | 'requires_approval'
  | 'not_allowed';

/** All valid access levels as a runtime array for validation. */
export const ALL_ACCESS_LEVELS: readonly AccessLevel[] = [
  'full',
  'view_only',
  'own_only',
  'requires_approval',
  'not_allowed',
] as const satisfies readonly AccessLevel[];

// ---------------------------------------------------------------------------
// LEGACY_ROLE_MAP — backward compatibility with old role names
// ---------------------------------------------------------------------------

/**
 * Maps legacy role names (from the existing auth system) to the new
 * `UserRole` values. Used during the migration period so that existing
 * sessions and API consumers continue to work.
 *
 * Mapping:
 *   admin   → admin
 *   support → normal_user
 *   finance → finance_user
 *   viewer  → normal_user
 */
export const LEGACY_ROLE_MAP: Readonly<Record<string, UserRole>> = {
  admin: 'admin',
  support: 'normal_user',
  finance: 'finance_user',
  viewer: 'normal_user',
};
